import React from 'react';
import { SelectOption } from 'global';
import { MultiSelect } from 'components/form';
import { Box, Button, FormHelperText, Stack } from '@mui/material';
import { Controller, useFieldArray, useForm } from 'react-hook-form';

import { vendorContactRoleHumanReadable } from '../../../../utils';
import { externalDashboardPortalDefaultHiddenFor } from '../constants';
import { ExternalDashboardPortalConfig, ExternalDashboardPortalForm } from '../types';

interface Props {
  isLoading: boolean;
  dashboardOptions: SelectOption[];
  config: ExternalDashboardPortalConfig['dashboardHiddenFor'];
  onConfirm: (configuration: ExternalDashboardPortalForm) => Promise<void>;
}

export const ExternalDashboardPortal = ({ isLoading, dashboardOptions, config, onConfirm }: Props) => {
  const initialValue = dashboardOptions.reduce<ExternalDashboardPortalForm>(
    (acc, { value }) => {
      acc.dashboards.push({
        dashboardId: value,
        hiddenFor: config[value] ?? externalDashboardPortalDefaultHiddenFor,
      });

      return acc;
    },
    { dashboards: [] }
  );

  const { control, handleSubmit } = useForm<ExternalDashboardPortalForm>({
    defaultValues: initialValue.dashboards.length
      ? initialValue
      : {
          dashboards: dashboardOptions.map(({ value }) => ({
            dashboardId: value,
            hiddenFor: externalDashboardPortalDefaultHiddenFor,
          })),
        },
  });

  const { fields } = useFieldArray({ control, name: 'dashboards' });
  const options = Object.entries(vendorContactRoleHumanReadable).map(([value, label]) => ({ label, value }));

  const externalDashboardMap = dashboardOptions.reduce<Record<string, string>>((acc, { value, label }) => {
    acc[value] = label;
    return acc;
  }, {});

  return (
    <Box sx={{ display: 'grid', gridTemplateRows: '1fr auto', gap: '1rem', height: '100%' }}>
      <Stack gap="0.5rem">
        {fields.map((field, index) => (
          <Controller
            key={field.id}
            control={control}
            name={`dashboards.${index}.hiddenFor`}
            render={({ field: controllerField }) => (
              <Stack>
                <MultiSelect
                  label="Hidden for:"
                  options={options}
                  formControlProps={{ variant: 'standard' }}
                  {...controllerField}
                />

                <FormHelperText>
                  {externalDashboardMap[field.dashboardId]} will be hidden for selected roles
                </FormHelperText>
              </Stack>
            )}
          />
        ))}
      </Stack>

      <Box>
        <Button loading={isLoading} variant="contained" onClick={handleSubmit(onConfirm)}>
          Save
        </Button>
      </Box>
    </Box>
  );
};
